import clsxm from '@/lib/clsxm';

import { IconColor } from '@/components/icons/base-icon';
import { IconDescriptiveLocationPin } from '@/components/icons/nomad-ui-icons';
import JobTag, { tagsTitleMap } from '@/components/job-tag';
import MoneyFormat from '@/components/MoneyFormat';
import Typography, { TypographyColor } from '@/components/typography';

import {
  checkIsCommonTag,
  checkIsDynamicTag,
  Job,
} from '@/services/public-jobs.service';
import {
  DISCIPLINE_TYPE_PARAM_TO_TITLE,
  isParamDisciplineType,
} from '@/utils/discipline-utils';
import { routes } from '@/utils/route-utils';

export type JobCardProps = Job & {
  full?: boolean;
};

export default function JobCard(props: JobCardProps) {
  const {
    contractWeeks,
    discipline,
    facility,
    full,
    id,
    location,
    shift,
    specialty,
    tags,
    weeklyPay,
    code,
    title,
  } = props;

  const hasAboveAvgPayTag = tags.includes('aboveAvgPay');
  const hasNewTag = tags.includes('new');
  const hasFeaturedTag = tags.includes('featured');

  const commonTags = tags.filter(
    (tag) => checkIsCommonTag(tag) || checkIsDynamicTag(tag)
  );

  const topPadding = 'pt-4';
  const leftPadding = 'pl-5';
  const xPadding = 'px-5';
  const marginBottom = 'mb-1.5';

  const jobUrl = routes.jobFull({
    discipline,
    specialty,
    code,
    title,
    id,
  });

  return (
    <a
      className={clsxm(
        'relative flex flex-col overflow-hidden pb-4',
        'm-2 cursor-pointer rounded-2xl bg-background shadow',
        full ? 'w-full' : 'w-[19rem]'
      )}
      href={jobUrl}
    >
      {hasAboveAvgPayTag && (
        <Typography.Body5
          className={clsxm('flex', topPadding, leftPadding)}
          color={TypographyColor.INDIGO}
        >
          {tagsTitleMap.aboveAvgPay}
        </Typography.Body5>
      )}

      {hasNewTag && (
        <JobTag
          tag='new'
          showIcon
          design='card-corner-right'
          className='absolute right-0 top-0'
        />
      )}

      <div
        className={clsxm(
          'flex flex-wrap items-center',
          marginBottom,
          leftPadding,
          !hasAboveAvgPayTag && topPadding
        )}
      >
        <Typography.Heading6>
          <MoneyFormat value={weeklyPay} />
        </Typography.Heading6>
        <Typography.Heading6>/wk</Typography.Heading6>
      </div>

      <Typography.Subtitle2 className={clsxm(xPadding, marginBottom)}>
        {isParamDisciplineType(discipline) &&
          DISCIPLINE_TYPE_PARAM_TO_TITLE[discipline] + ' - '}
        {specialty}
      </Typography.Subtitle2>

      <div className='relative'>
        <Typography.Body35
          className={clsxm(
            'line-clamp-1 truncate !break-all',
            xPadding,
            marginBottom
          )}
        >
          {shift} • {contractWeeks} weeks • {facility}
        </Typography.Body35>
      </div>

      {commonTags.length !== 0 && (
        <div className={clsxm('flex flex-wrap gap-1.5', xPadding)}>
          {commonTags.map((tag, index) => (
            <JobTag key={index} tag={tag} />
          ))}
        </div>
      )}

      <div className='h-2' />

      <hr
        className={clsxm('my-3 mt-auto h-px border-0 bg-gray-200', xPadding)}
      />

      <div className={clsxm('flex', xPadding)}>
        <IconDescriptiveLocationPin color={IconColor.TEXT} /> {location.city},{' '}
        {location.state}
      </div>

      {hasFeaturedTag && (
        <Typography.Body5
          className={clsxm('flex', leftPadding)}
          color={TypographyColor.INDIGO}
        >
          {tagsTitleMap.featured}
        </Typography.Body5>
      )}
    </a>
  );
}
